'use client';

import React, { useState, useEffect } from 'react';

import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  id: string;
  label: string;
  sectionId: string;
  orbitalIntegration: boolean;
}

interface AnchorNavigationProps {
  className?: string;
}

const AnchorNavigation = ({ className = '' }: AnchorNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>('ecosystem');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const navigationItems: NavigationItem[] = [
    { id: 'ecosystem', label: 'Ecosystem', sectionId: 'ecosystem-section', orbitalIntegration: true },
    { id: 'investment', label: 'Investment', sectionId: 'investment-section', orbitalIntegration: false },
    { id: 'roadmap', label: 'Roadmap', sectionId: 'roadmap-section', orbitalIntegration: false },
    { id: 'security', label: 'Security', sectionId: 'security-section', orbitalIntegration: false },
    { id: 'community', label: 'Community', sectionId: 'community-section', orbitalIntegration: false },
  ];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const navItem = navigationItems.find((item) => item.sectionId === sectionId);
          if (navItem) {
            setActiveSection(navItem.id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    navigationItems.forEach((item) => {
      const section = document.getElementById(item.sectionId);
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleNavClick = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <nav data-component-id="src/components/common/AnchorNavigation.tsx:77:6" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="77" data-component-end-line="99" data-component-file="AnchorNavigation.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
        className={`fixed top-8 right-8 z-[100] hidden lg:block ${className}`}
        aria-label="Main navigation"
      >
        <div data-component-id="src/components/common/AnchorNavigation.tsx:81:8" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="81" data-component-end-line="98" data-component-file="AnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%2F95%20backdrop-blur-sm%20rounded-lg%20shadow-card%20border%20border-border%20px-6%20py-4%22%7D" className="bg-card/95 backdrop-blur-sm rounded-lg shadow-card border border-border px-6 py-4">
          <ul data-component-id="src/components/common/AnchorNavigation.tsx:82:10" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="82" data-component-end-line="97" data-component-file="AnchorNavigation.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22flex%20flex-col%20gap-4%22%7D" className="flex flex-col gap-4">
            {navigationItems.map((item) => (
              <li data-component-id="src/components/common/AnchorNavigation.tsx:84:14" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="84" data-component-end-line="95" data-component-file="AnchorNavigation.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={item.id}>
                <button data-component-id="src/components/common/AnchorNavigation.tsx:85:16" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="85" data-component-end-line="94" data-component-file="AnchorNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  onClick={() => handleNavClick(item.sectionId)}
                  className={`font-rajdhani font-semibold text-sm tracking-wide transition-all duration-250 hover:text-accent ${
                    activeSection === item.id
                      ? 'text-accent border-l-2 border-accent pl-3' :'text-text-secondary pl-3'
                  }`}
                  aria-current={activeSection === item.id ? 'true' : 'false'}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <button data-component-id="src/components/common/AnchorNavigation.tsx:101:6" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="101" data-component-end-line="112" data-component-file="AnchorNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22fixed%20top-6%20right-6%20z-%5B100%5D%20lg%3Ahidden%20bg-card%20rounded-lg%20p-3%20shadow-card%20border%20border-border%22%7D"
        onClick={toggleMobileMenu}
        className="fixed top-6 right-6 z-[100] lg:hidden bg-card rounded-lg p-3 shadow-card border border-border"
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        <Icon data-component-id="src/components/common/AnchorNavigation.tsx:107:8" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="107" data-component-end-line="111" data-component-file="AnchorNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-accent%22%7D"
          name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
          size={24}
          className="text-accent"
        />
      </button>

      {isMobileMenuOpen && (
        <div data-component-id="src/components/common/AnchorNavigation.tsx:115:8" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="115" data-component-end-line="143" data-component-file="AnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-%5B90%5D%20lg%3Ahidden%22%7D"
          className="fixed inset-0 z-[90] lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div data-component-id="src/components/common/AnchorNavigation.tsx:119:10" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="119" data-component-end-line="119" data-component-file="AnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-background%22%7D" className="absolute inset-0 bg-background" />
          <div data-component-id="src/components/common/AnchorNavigation.tsx:120:10" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="120" data-component-end-line="142" data-component-file="AnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20right-0%20w-64%20h-full%20bg-card%20shadow-card%20border-l%20border-border%20animate-slide-in%22%7D"
            className="absolute top-0 right-0 w-64 h-full bg-card shadow-card border-l border-border animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div data-component-id="src/components/common/AnchorNavigation.tsx:124:12" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="124" data-component-end-line="141" data-component-file="AnchorNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-24%20px-6%22%7D" className="pt-24 px-6">
              <ul data-component-id="src/components/common/AnchorNavigation.tsx:125:14" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="125" data-component-end-line="140" data-component-file="AnchorNavigation.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22flex%20flex-col%20gap-6%22%7D" className="flex flex-col gap-6">
                {navigationItems.map((item) => (
                  <li data-component-id="src/components/common/AnchorNavigation.tsx:127:18" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="127" data-component-end-line="138" data-component-file="AnchorNavigation.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={item.id}>
                    <button data-component-id="src/components/common/AnchorNavigation.tsx:128:20" data-component-path="src/components/common/AnchorNavigation.tsx" data-component-line="128" data-component-end-line="137" data-component-file="AnchorNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                      onClick={() => handleNavClick(item.sectionId)}
                      className={`font-rajdhani font-semibold text-lg tracking-wide transition-all duration-250 hover:text-accent w-full text-left ${
                        activeSection === item.id
                          ? 'text-accent border-l-2 border-accent pl-4' :'text-text-secondary pl-4'
                      }`}
                      aria-current={activeSection === item.id ? 'true' : 'false'}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnchorNavigation;